import './Listbox.scss'
import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject
} from 'react'
import { MdOutlineCheck } from 'react-icons/md'
import { clx } from '../clx'
import { observeMovement, placeAnchored, Portal, rectOf } from '../layer'
import { Spinner } from '../Spinner'
import { useWindow } from '../virtual'
import type { ListboxModel, ListboxOption, ListboxOptionState } from './model'

/** Rows are 2.75rem (the hit target, see Listbox.scss); windowing needs px. */
const ROW_REM = 2.75

function remToPx(rem: number): number {
  const root = Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
  return (Number.isFinite(root) && root > 0 ? root : 16) * rem
}

export interface ListboxPopupProps {
  /** id of the role="listbox" element; options get `${id}-${rowIndex}`. */
  id: string
  /** The control the popup is anchored to (start-aligned, same width). */
  anchorRef: RefObject<HTMLElement | null>
  /** The combobox input; its <label> names the listbox. */
  inputRef: RefObject<HTMLInputElement | null>
  model: ListboxModel
  activeIndex: number
  isSelected: (option: ListboxOption) => boolean
  onPick: (option: ListboxOption) => void
  onActiveChange: (index: number) => void
  /** The anchor scrolled out of view. */
  onDismiss: () => void
  renderOption?: ((option: ListboxOption, state: ListboxOptionState) => ReactNode) | undefined
  isMulti?: boolean
  isLoading?: boolean
  emptyText?: ReactNode
  size?: 'sm' | 'md'
}

/** id of the option element for a row. */
export const optionId = (listId: string, index: number) => `${listId}-${index}`

/**
 * The portalled listbox popup shared by Combobox and MultiSelect. Fixed
 * height rows, windowed above ~200 (useWindow); the active row is always
 * rendered so aria-activedescendant never points at nothing.
 */
export function ListboxPopup({
  id,
  anchorRef,
  inputRef,
  model,
  activeIndex,
  isSelected,
  onPick,
  onActiveChange,
  onDismiss,
  renderOption,
  isMulti = false,
  isLoading = false,
  emptyText = 'No results',
  size = 'md'
}: ListboxPopupProps) {
  const { rows } = model
  const surfaceRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [rowHeight] = useState(() => remToPx(ROW_REM))
  const win = useWindow(listRef, { count: rows.length, rowHeight })
  const { scrollToIndex } = win

  const dismiss = useEffectEvent(onDismiss)

  useLayoutEffect(() => {
    const anchor = anchorRef.current
    const surface = surfaceRef.current
    const list = listRef.current
    if (!anchor || !surface || !list) return
    // Name the listbox after the combobox: its <label>, else its aria-label.
    const input = inputRef.current
    const label =
      input?.labels?.[0]?.textContent.replace(/\s*\*$/, '').trim() ||
      input?.getAttribute('aria-label')
    if (label) list.setAttribute('aria-label', label)
    return observeMovement(anchor, surface, () => {
      const rect = rectOf(anchor)
      const viewport = { width: window.innerWidth, height: window.innerHeight }
      if (viewport.height > 0 && (rect.top + rect.height < 0 || rect.top > viewport.height)) {
        dismiss()
        return
      }
      surface.style.setProperty('--listbox-anchor-width', `${rect.width}px`)
      const p = placeAnchored(
        rect,
        { width: surface.offsetWidth, height: surface.offsetHeight },
        viewport,
        {
          align: 'start'
        }
      )
      surface.style.setProperty('--listbox-top', `${p.top}px`)
      surface.style.setProperty('--listbox-left', `${p.left}px`)
      surface.style.setProperty('--listbox-available', `${p.available}px`)
      surface.dataset.side = p.side
    })
  }, [anchorRef, inputRef])

  // The row the pointer made active. It is under the pointer already:
  // scrolling it into view would move the list under the pointer, which
  // activates another row, and so on. Only keyboard moves scroll.
  const pointerIndex = useRef(-1)
  useEffect(() => {
    if (activeIndex < 0 || activeIndex === pointerIndex.current) return
    scrollToIndex(activeIndex)
  }, [activeIndex, scrollToIndex])

  // The window, plus the active row when it lies outside it.
  const indices: number[] = []
  for (let i = win.start; i < win.end; i++) indices.push(i)
  if (
    activeIndex >= 0 &&
    activeIndex < rows.length &&
    (activeIndex < win.start || activeIndex >= win.end)
  ) {
    indices.push(activeIndex)
    indices.sort((a, b) => a - b)
  }

  const renderRow = (index: number) => {
    const row = rows[index]
    if (!row) return null
    if (row.kind === 'header') {
      return (
        <div key={index} data-index={index} className="listbox__header" aria-hidden="true">
          {row.label}
        </div>
      )
    }
    const { option } = row
    const state = { isActive: index === activeIndex, isSelected: isSelected(option) }
    return (
      <div
        key={index}
        id={optionId(id, index)}
        role="option"
        data-index={index}
        aria-selected={state.isSelected}
        aria-disabled={option.disabled ? true : undefined}
        data-active={state.isActive || undefined}
        className={clx(
          'listbox__option',
          option.depth === 1 && 'listbox__option--is-nested',
          option.isEmphasized && 'listbox__option--is-emphasized'
        )}
        onMouseMove={() => {
          if (state.isActive || option.disabled) return
          pointerIndex.current = index
          onActiveChange(index)
        }}
        onClick={() => {
          if (!option.disabled) onPick(option)
        }}
      >
        {isMulti && (
          <span className="listbox__check" aria-hidden="true">
            {state.isSelected && <MdOutlineCheck />}
          </span>
        )}
        <span className="listbox__label">
          {renderOption ? renderOption(option, state) : option.label}
        </span>
      </div>
    )
  }

  // Runs of consecutive rows in one group become a role="group"; a gap
  // in the rendered indices becomes a spacer so the scrollbar stays true.
  const children: ReactNode[] = []
  const usedKeys = new Set<string>()
  let segment: number[] = []
  let segmentGroup = -1
  const flush = () => {
    if (!segment.length) return
    if (segmentGroup === -1) children.push(...segment.map(renderRow))
    else {
      const header = rows[segmentGroup]
      let key = `g${segmentGroup}`
      while (usedKeys.has(key)) key += '+'
      usedKeys.add(key)
      children.push(
        <div
          key={key}
          role="group"
          aria-label={header?.kind === 'header' ? header.label : undefined}
          className="listbox__group"
        >
          {segment.map(renderRow)}
        </div>
      )
    }
    segment = []
  }
  const spacer = (rowsSkipped: number, key: string) => {
    if (rowsSkipped > 0)
      children.push(
        <div
          key={key}
          className="listbox__spacer"
          aria-hidden="true"
          style={{ blockSize: rowsSkipped * rowHeight }}
        />
      )
  }
  let previous = -1
  for (const index of indices) {
    const row = rows[index]
    if (!row) continue
    if (index - previous > 1 || row.groupKey !== segmentGroup) flush()
    spacer(index - previous - 1, `s${previous}`)
    segmentGroup = row.groupKey
    segment.push(index)
    previous = index
  }
  flush()
  spacer(rows.length - 1 - previous, 's-end')

  const status = isLoading ? (
    <>
      <Spinner size="sm" />
      Loading…
    </>
  ) : rows.length === 0 ? (
    emptyText
  ) : null

  return (
    <Portal>
      <div
        ref={surfaceRef}
        className={clx('listbox', size === 'sm' && 'listbox--is-sm')}
        onMouseDown={(event) => {
          // Keep focus (and the caret) in the combobox input.
          event.preventDefault()
        }}
      >
        {status !== null && (
          <div className="listbox__status" role="status">
            {status}
          </div>
        )}
        <div
          ref={listRef}
          id={id}
          role="listbox"
          aria-multiselectable={isMulti || undefined}
          className="listbox__list"
          data-virtual={win.isVirtual || undefined}
          hidden={rows.length === 0}
        >
          {children}
        </div>
      </div>
    </Portal>
  )
}
